/**
 * The Rule Element engine (ADR-0001): a pure function from a Character and the Entries its
 * Selections resolve to (plus extra Entries fed by play mode) to a derived sheet in which every
 * value carries its breakdown. No DOM, no storage.
 *
 * Phases follow Foundry's character data preparation:
 *   1. structural data from ancestry, heritage, background, class and feats; boosts -> abilities
 *   2. ActiveEffectLike / RollOption in the applyAEs phase; numeric roll options
 *   3. FlatModifier registration (and the beforeDerived phase)
 *   4. statistics: HP, saves, Perception, skills, class DC, AC, speed, initiative
 *   5. the afterDerived phase
 */
import type { Entry } from "../../shared/snapshot-format";
import type { AnswerValue, Character, Choice, ChoiceSource, LevelNumber, Override, ToggleState, VariantRules } from "../character/model";
import { choiceKey as keyOfChoice, isLevelNumber, parseChoiceKey } from "../character/model";
import { applyAutomaticBonusProgression, skillPotencies, type SkillPotencyAnswer } from "./abp";
import { createActorData, getProperty, type ActorData, type AutoChange, type CraftingEntryData, type MartialProficiencyData } from "./actor-data";
import { applyAlteration, type ItemAlteration } from "./alterations";
import type { DamageDice } from "./damage-dice";
import type { IWRException } from "./iwr";
import { seedManualIWR } from "./iwr";
import type { Catalogue } from "./catalogue";
import { prepareConditionSiblings, setEffectRollOptions } from "./conditions";
import { armorCheckPenalty, armorClassModifiers, armorClassRank, armorDexterityCap, armorSaveModifiers, equipmentSpeedModifiers, setArmorRollOptions, setStrengthRequirementOption, type EquippedInput } from "./equipment";
import { buildInventory, emptyInventoryState, inventoryView, prepareBulk, prepareHands, prepareInventoryActorData, type InventoryItemView, type InventoryView } from "./inventory";
import { EnginePhysical } from "./physical";
import { damageTypeLabel } from "./labels";
import { EngineItem, type EngineItemInput } from "./items";
import { createAttributeModifier, createModifier, resolveModifiers, signed, type Modifier } from "./modifiers";
import type { RawPredicate } from "./predicate";
import { prepareMovement, type DerivedSpeed } from "./movement";
import { prepareAncestry, prepareBackground, prepareBuildData, prepareClass, prepareFeat, prepareHeritage, setDerivedRollOptions, setNumericRollOptions, prepareDeity } from "./prepare";
import { applyRankOverrides, prepareMartialProficiencies, proficiencyLabel, rankOverrideKey, type ProficiencyKind } from "./proficiencies";
import { prepareSenses, senseViews, type SenseView } from "./senses";
import { RollOptions } from "./roll-options";
import type { RuleInstance, RuleSource } from "./rule-elements/base";
import { isItemUuid } from "./rule-elements/choice-set";
import { collectPrompts, createRules, createRulesFor, readSelector, runPhase, type ChoicePrompt } from "./rules";
export type { ChoicePrompt } from "./rules";
import { grantFlag, resolveGrantUuid, resolvedPreselections, type DeleteAction, type GrantItemData } from "./rule-elements/grant-item";
import { classifyKey, type RuleSupport } from "./rule-elements/registry";
import { sluggify } from "./slug";
import type { DerivedStatistics, EngineState, RollOptionToggle } from "./state";
import { breakdownOf, buildStatistic, type CheckEphemeralEffect, type DerivedStatistic } from "./statistics";
import { createOverrideLookup, damageDiceView, extrasView, iwrView, modifierView, noteView, speedView as buildSpeedView, statisticView as buildStatisticView } from "./views";
import { deriveFamiliar, familiarAbilitiesWithout, isFamiliarAbilityChoice, type FamiliarChoice, type FamiliarView } from "./familiar";
export type { FamiliarView, FamiliarAbilityView, FamiliarSlotsView } from "./familiar";
import { prepareStrikes, type DerivedStrike, type DerivedStrikeDamage } from "./strikes";
import { collectCastingSources, spellcastingStatistics, spellcastingViews, type SpellcastingEntryView } from "./spellcasting";
import { createSynthetics, extractModifierAdjustments, extractModifiers, type AuraData, type DegreeOfSuccessAdjustmentSynthetic, type FastHealingSynthetic, type LoseHitPointsSynthetic, type SpecialResourceData, type TempHPSynthetic } from "./synthetics";
import type { EquippedWeaponInput } from "./weapons";
import { ATTRIBUTES, ATTRIBUTE_LABELS, MOVEMENT_TYPES, PROFICIENCY_RANKS, SAVES, SAVE_LABELS, SKILL_SLUGS, isAttribute, isSkillSlug, type Attribute, type MovementType, type Rank, type SaveSlug } from "./values";

export interface EngineInput {
  character: Character;
  /** The Entry for every Selection (and extra) UUID. Selections with no Entry are reported as missing. */
  entries: ReadonlyMap<string, Entry>;
  /** Entries applied on top of the progression: Conditions and Effects from play mode (#18). */
  extra?: EngineItemInput[];
  /** Toggle states chosen at the table (a Session, #18), keyed `${domain}:${option}`, over the Character's own `toggles`. A boolean is `checked`. */
  toggleState?: Record<string, boolean | ToggleState>;
  /** The catalogue index and config tables, for ChoiceSet Pack queries and labels. Optional. */
  catalogue?: Catalogue;
  /** Which ChoiceSets get their candidate lists computed: only open ones (default) or every one (guide time). */
  prompts?: "open" | "all";
  /** Physical Entries fed directly (tests): worn armour, a held shield, carried weapons. The Character's inventory is the app's source. */
  equipped?: EquippedInput;
}

export { type EquippedWeaponInput, type InventoryView, type InventoryItemView };

export interface ModifierView {
  slug: string;
  label: string;
  type: string;
  value: number;
  enabled: boolean;
  ignored: boolean;
  hideIfDisabled: boolean;
  /** Source Entry UUID, null for engine-generated modifiers. */
  source: string | null;
  sourceName: string | null;
  ability: Attribute | null;
  predicate: unknown[];
  /** The Entries whose AdjustModifier changed or suppressed this modifier. */
  adjustedBy: string[];
  /** Damage modifiers only: the damage type and category (persistent, precision, splash). */
  damageType: string | null;
  damageCategory: string | null;
  /** Damage modifiers only: applies on a critical hit only (true), never doubles (false), or doubles (null). */
  critical: boolean | null;
  /** The Effect that produced this modifier ends once a Roll uses it (Foundry: FlatModifier `removeAfterRoll`). */
  removeAfterRoll: boolean;
}

/** A Note attached to a statistic's rolls, with whether its predicate passes for the statistic's own options. */
export interface NoteView {
  selector: string;
  title: string | null;
  text: string;
  predicate: RawPredicate;
  outcome: string[];
  visibility: "gm" | "owner" | null;
  enabled: boolean;
  /** The predicate names the rolled total: enabled subject to it, tested again once the die is rolled (#19). */
  postRoll: boolean;
  source: string | null;
  sourceName: string | null;
}

export interface DegreeAdjustmentView {
  adjustments: DegreeOfSuccessAdjustmentSynthetic["adjustments"];
  predicate: RawPredicate;
  enabled: boolean;
  postRoll: boolean;
  source: string | null;
  sourceName: string | null;
}

export interface SubstitutionView {
  slug: string;
  label: string;
  value: number;
  required: boolean;
  effectType: "fortune" | "misfortune";
  predicate: RawPredicate;
  enabled: boolean;
  /** The Effect ends after a Roll: always, or only when the substitution was used. */
  removeAfterRoll: boolean | "if-enabled";
  source: string | null;
  sourceName: string | null;
}

/** A RollTwice Rule Element on the statistic (fortune or misfortune), with whether its predicate passes. */
export interface RollTwiceView {
  keep: "higher" | "lower";
  predicate: RawPredicate;
  enabled: boolean;
  removeAfterRoll: boolean;
  source: string | null;
  sourceName: string | null;
}

export interface EphemeralEffectView {
  uuid: string;
  name: string | null;
  affects: "target" | "origin";
  predicate: RawPredicate;
  enabled: boolean;
  source: string | null;
  sourceName: string | null;
}

/** What a Roll of a check consumes beyond its modifiers (#19). */
export interface CheckExtrasView {
  notes: NoteView[];
  degreeAdjustments: DegreeAdjustmentView[];
  rollTwice: "keep-higher" | "keep-lower" | false;
  rollTwiceSources: RollTwiceView[];
  substitutions: SubstitutionView[];
  ephemeralEffects: EphemeralEffectView[];
}

export interface StatisticView extends CheckExtrasView {
  slug: string;
  label: string;
  attribute: Attribute | null;
  rank: Rank | null;
  lore: boolean;
  /** The engine's value: the check modifier, or the DC for AC and class DC. */
  derived: number;
  /** The Override in force, if any. */
  override: Override | null;
  /** What the sheet shows: the Override's value, else the derived value. */
  value: number;
  modifiers: ModifierView[];
  breakdown: string;
  dc: number;
  dcModifiers: ModifierView[];
  dcBreakdown: string;
  /** The key an Override on this statistic uses. */
  overrideKey: string;
}

export interface AbilityView {
  attribute: Attribute;
  label: string;
  derived: number;
  base: number;
  override: Override | null;
  value: number;
  overrideKey: string;
}

export interface HitPointsView {
  derived: number;
  override: Override | null;
  value: number;
  modifiers: ModifierView[];
  breakdown: string;
  overrideKey: "hp-max";
  /** Rest recovery tweaks an Entry may set (Foundry: `hp.recoveryMultiplier`, `hp.recoveryAddend`). */
  recoveryMultiplier: number;
  recoveryAddend: number;
  negativeHealing: boolean;
}

/** The death track (Foundry: `attributes.dying`, `wounded`, `doomed`) from the Conditions held. */
export interface DeathTrackView {
  dying: { value: number; max: number; recoveryDC: number };
  wounded: { value: number; max: number };
  doomed: { value: number; max: number };
}

/**
 * A grant a Session item (a Condition or Effect fed as an extra Entry) asks for: dying grants
 * unconscious, an Effect grants a Condition. The Session materialises these (#18) as the
 * Character reconciler does for progression grants.
 */
export interface TemporaryGrantView {
  /** The Session id of the granter. */
  granterId: string;
  granter: { uuid: string; name: string };
  index: number;
  uuid: string | null;
  name: string | null;
  type: string | null;
  slug: string | null;
  wanted: boolean;
  /** A Session item with this granter and Entry is held (or, with `allowDuplicate` off, any item of the Entry). */
  present: boolean;
  loaded: boolean;
  allowDuplicate: boolean;
  onDelete: { granter: DeleteAction; grantee: DeleteAction };
  alterations: ItemAlteration[];
  /** The granted Condition's value after alterations, when the Entry is loaded and valued. */
  badge: number | null;
  /** The granted Effect's duration, when the Entry is a loaded Effect. */
  duration: { unit: string; value: number; expiry: string | null; sustained: boolean } | null;
}

export interface SpeedView {
  type: MovementType;
  /** "Land Speed", "Fly Speed", ... */
  label: string;
  base: number;
  /** The Entry the base came from: the ancestry, or the BaseSpeed's Entry. */
  source: string | null;
  derived: number;
  override: Override | null;
  value: number;
  modifiers: ModifierView[];
  breakdown: string;
  /** The speed this one is derived from (a BaseSpeed formula over another speed's total), else null. */
  derivedFrom: MovementType | null;
  overrideKey: string;
}

/** One attack or defense proficiency: a category from the class Entry, or a custom one a MartialProficiency added. */
export interface ProficiencyView {
  slug: string;
  kind: ProficiencyKind;
  label: string;
  /** The rank in force: the Override's, else the derived one. */
  rank: Rank;
  /** The rank before the Override replaced it: from the class Entry and features, or for a linked proficiency its link. */
  derived: Rank;
  override: Override | null;
  overrideKey: string;
  /** The proficiency bonus at the Character's Level (0 when untrained). */
  value: number;
  breakdown: string;
  visible: boolean;
  /** Added by a MartialProficiency Rule Element (has a `definition`) rather than a category. */
  custom: boolean;
  /** Which weapons or armour count for a custom proficiency: a predicate over an item's roll options. */
  definition: RawPredicate | null;
  /** The category whose rank this proficiency follows, if linked. */
  sameAs: string | null;
  /** The highest rank a linked proficiency reaches. */
  maxRank: Rank | null;
  source: string | null;
  sourceName: string | null;
}

export type SpeedViews = { land: SpeedView } & Record<Exclude<MovementType, "land">, SpeedView | null>;

export interface DamageDiceView {
  slug: string;
  label: string;
  diceNumber: number;
  dieSize: string | null;
  damageType: string | null;
  category: string | null;
  critical: boolean | null;
  enabled: boolean;
  hideIfDisabled: boolean;
  override: DamageDice["override"];
  tags: string[];
  source: string | null;
  sourceName: string | null;
}

export interface StrikeDamageView {
  /** Foundry's roll formulas, per degree of success ("{2d8 + 4}[slashing]"). */
  formula: { failure: string | null; success: string | null; criticalSuccess: string | null };
  /** The breakdown Foundry shows for each formula. */
  breakdown: { failure: string[]; success: string[]; criticalSuccess: string[] };
  base: { diceNumber: number; dieSize: string | null; modifier: number; damageType: string; damageTypeLabel: string; category: string | null }[];
  dice: DamageDiceView[];
  modifiers: ModifierView[];
  materials: string[];
  domains: string[];
  notes: NoteView[];
  ephemeralEffects: EphemeralEffectView[];
  criticalSpecialization: boolean;
  /** Resistances the damage ignores, from property runes (#19); Infinity means all of it. */
  ignoredResistances: { type: string; max: number }[];
}

/** A spell's damage at its cast rank (#19 rolls it): Foundry's formulas per degree with the synthetics that fed them. */
export interface SpellDamageView {
  formula: { failure: string | null; success: string | null; criticalSuccess: string | null };
  breakdown: { failure: string[]; success: string[]; criticalSuccess: string[] };
  modifiers: ModifierView[];
  dice: DamageDiceView[];
  kinds: ("damage" | "healing")[];
  /** False when the spell's formula is not plain dice and constants: the text is rolled as written, without modifiers. */
  simple: boolean;
}

export interface StrikeVariantView {
  penalty: number;
  total: number;
  label: string;
}

/** One attack the Character can make: the weapon, its attack modifier with the MAP variants, and its damage. */
export interface StrikeView extends CheckExtrasView {
  slug: string;
  label: string;
  /** The Entry it comes from: the weapon, or the Entry whose Strike Rule Element made it; null for the basic unarmed attack. */
  source: string | null;
  sourceName: string | null;
  category: string;
  group: string | null;
  baseType: string | null;
  /** The weapon's traits, sorted; the action traits (attack, ...) are separate. */
  traits: string[];
  actionTraits: string[];
  melee: boolean;
  ranged: boolean;
  thrown: boolean;
  rangeIncrement: number | null;
  maxRange: number | null;
  handsHeld: number;
  ready: boolean;
  visible: boolean;
  handsAvailable: boolean;
  quantity: number;
  proficiencyRank: Rank;
  /** The engine's attack modifier. */
  derived: number;
  override: Override | null;
  /** What the sheet shows: the Override's value, else the derived value. */
  value: number;
  modifiers: ModifierView[];
  breakdown: string;
  domains: string[];
  /** Attack totals at 0, 1 and 2 Multiple Attack Penalty increases; #19 rolls these. */
  variants: [StrikeVariantView, StrikeVariantView, StrikeVariantView];
  map: { map1: number; map2: number; label: string };
  damage: StrikeDamageView | null;
  versatileOptions: { value: string; selected: boolean; label: string }[];
  altUsages: StrikeView[];
  battleForm: boolean;
  runes: { potency: number; striking: number; property: string[] };
  overrideKey: string;
  /** The inventory item the strike comes from (a weapon or a shield), when it does. */
  inventoryId: string | null;
  /** What ammunition the weapon takes and the compatible stacks held (#18 fires them); null when it needs none. */
  ammunition: { baseType: string | null; selected: string | null; compatible: { id: string; name: string; quantity: number }[] } | null;
}

export interface IWRView {
  kind: "immunity" | "weakness" | "resistance";
  type: string;
  value: number | null;
  exceptions: IWRException[];
  doubleVs: IWRException[];
  definition: RawPredicate | null;
  /** "fire 5 (except cold iron)". */
  label: string;
  source: string | null;
  sourceName: string | null;
  applyOnce: boolean;
  /** Entered by hand on the Character (#22): the value as entered and the note. A Rule Element that raised the value names itself in `sourceName`. */
  manual: { value: number | null; note: string | null } | null;
}

export interface TempHPView extends TempHPSynthetic {}
export interface FastHealingView extends FastHealingSynthetic {}
export interface LoseHitPointsView extends LoseHitPointsSynthetic {}
export interface SpecialResourceView extends SpecialResourceData {}

/** The resources block: what the Session (#18) draws on. */
export interface ResourcesView {
  tempHP: TempHPView[];
  fastHealing: FastHealingView[];
  loseHitPoints: LoseHitPointsView[];
  /** Hit points that cannot be recovered while their source lasts. */
  unrecoverableHP: number;
  special: Record<string, SpecialResourceView>;
  crafting: Record<string, CraftingEntryData>;
  heroPoints: { value: number; max: number };
  focus: { value: number; max: number; cap: number };
}

export interface AuraView extends AuraData {}

export interface UnsupportedRule {
  key: string;
  support: Exclude<RuleSupport, "implemented" | "canvas-only">;
  uuid: string;
  name: string;
  index: number;
  /** The selector or path the Rule Element names, when one can be read; for a stopped grant, the chain. */
  selector: string | null;
}

/** One grant a GrantItem or a feature list asks for, and whether the Character holds it. */
export interface GrantView {
  /** The key of the Choice that holds (or would hold) the granted Selection. */
  key: string;
  level: LevelNumber;
  source: Extract<ChoiceSource, { kind: "rule-element" | "feature" }>;
  granter: { uuid: string; name: string; choiceKey: string | null };
  /** The granted Entry, or null when the UUID could not be resolved. */
  uuid: string | null;
  name: string | null;
  /** The predicate passes (or there is none) and the granter is active. */
  wanted: boolean;
  /** The Character already holds this grant. */
  present: boolean;
  /** The granted Entry was supplied to the engine. */
  loaded: boolean;
  flag: string | null;
  preselect: Record<string, string | number>;
  onDelete: { granter: DeleteAction; grantee: DeleteAction };
  alterations: ItemAlteration[];
  allowDuplicate: boolean;
  reevaluateOnUpdate: boolean;
  /** Why the engine refuses to follow this grant (a chain deeper than 16 or a cycle), else null. */
  stopped: string | null;
  /** The granted feat's level for a feature-list row (its `levelTaken`). */
  levelTaken: number | null;
}

export interface RuleProblem {
  key: string;
  uuid: string;
  name: string;
  index: number;
  messages: string[];
}

export interface ItemSummary {
  uuid: string;
  /** The name as shown: adjusted by a ChoiceSet's `adjustName` where one applies. */
  name: string;
  type: string;
  choiceKey: string | null;
  level: LevelNumber | null;
  rules: number;
  /** The Entry that granted this one, when it was granted. */
  grantedBy: { uuid: string; name: string; choiceKey: string | null } | null;
  /** Answers still needed on this Entry (open ChoiceSets), by flag. */
  openPrompts: string[];
  /** Alterations applied to this copy, in words ("trait +divine (Kitsune)"). */
  alterations: string[];
  /** An in-memory Entry: an aura's own Effect or an in-memory Condition, not a Selection. */
  inMemory: boolean;
  /** The Session item this Entry stands for, when fed by play mode (#18). */
  sessionId: string | null;
  /** False for a Condition another Condition deactivated; its Rule Elements do not apply. */
  active: boolean;
  overriddenBy: string[];
  /** The badge: a Condition's value, an Effect's counter. */
  badge: number | null;
}

export interface DerivedSheet {
  level: number;
  abilities: Record<Attribute, AbilityView>;
  hp: HitPointsView;
  ac: StatisticView;
  saves: Record<SaveSlug, StatisticView>;
  perception: StatisticView;
  initiative: StatisticView;
  skills: Record<string, StatisticView>;
  classDC: StatisticView | null;
  classDCs: Record<string, StatisticView>;
  /** The base spellcasting proficiency every Spellcasting Entry extends. Null when untrained. */
  baseSpellcasting: StatisticView | null;
  /** The Spellcasting Entries (#10): one per Casting Source, with statistic, slots, spells and open spell Choices. */
  spellcasting: SpellcastingEntryView<StatisticView>[];
  /** Casting Sources held that the app's casting table does not describe; a manual Spellcasting Entry stands in. */
  unrecognisedCasting: { uuid: string; name: string; reason: string }[];
  speeds: SpeedViews;
  /** Every attack and defense proficiency, by slug: the class's categories plus custom proficiencies. */
  proficiencies: { attacks: Record<string, ProficiencyView>; defenses: Record<string, ProficiencyView> };
  strikes: StrikeView[];
  iwr: { immunities: IWRView[]; weaknesses: IWRView[]; resistances: IWRView[] };
  resources: ResourcesView;
  /** Statistics SpecialStatistic Rule Elements built, by slug. */
  specialStatistics: Record<string, StatisticView>;
  auras: AuraView[];
  /** The battle form in force, if any. */
  battleForm: { uuid: string; name: string; ownUnarmed: boolean } | null;
  reach: { base: number; manipulate: number };
  /** The lowest Dexterity modifier cap on AC, if any. */
  dexterityCap: { value: number; source: string } | null;
  size: string;
  traits: string[];
  /** The senses to show, with labels; vision a better vision supersedes is left out. */
  senses: SenseView[];
  languages: string[];
  rollOptions: Record<string, string[]>;
  toggles: RollOptionToggle[];
  unsupported: UnsupportedRule[];
  problems: RuleProblem[];
  autoChanges: Record<string, AutoChange[]>;
  items: ItemSummary[];
  missing: { uuid: string; choiceKey: string }[];
  /** Overrides on the Character that name no statistic on this sheet. */
  danglingOverrides: Override[];
  prompts: ChoicePrompt[];
  grants: GrantView[];
  /** Grants asked for by Session items (#18). */
  temporaryGrants: TemporaryGrantView[];
  /** The inventory (#11): every physical Entry owned, bulk, wealth, hands and investiture. */
  inventory: InventoryView;
  death: DeathTrackView;
  /** Entry UUIDs the engine wanted and was not given (grants, in-memory Conditions). Load them and derive again. */
  needed: string[];
  /** The Familiar (#13): its statistics and abilities, or null when nothing gives the Character one. */
  familiar: FamiliarView | null;
}

const ROLE_ORDER: Record<EngineItem["role"], number> = { ancestry: 0, heritage: 1, background: 2, class: 3, other: 4, physical: 5, temporary: 6 };
/** The roll option each Variant Rule sets while on: `variant:<slug>`. */
const VARIANT_OPTION_SLUGS: Record<keyof VariantRules, string> = { freeArchetype: "free-archetype", automaticBonusProgression: "abp", gradualAttributeBoosts: "gradual-attribute-boosts", ancestryParagon: "ancestry-paragon", dualClass: "dual-class", proficiencyWithoutLevel: "proficiency-without-level" };
const BOOST_BUCKETS = [1, 5, 10, 15, 20] as const;

function choiceKeyOf(level: LevelNumber, choice: Choice): string {
  return keyOfChoice(level, choice.source);
}

/** Foundry follows grant chains without limit; this engine stops at this depth (issue #6). */
export const MAX_GRANT_DEPTH = 16;

const ABC_TYPES = ["ancestry", "background", "class"];

/** Derive the sheet. Never throws for malformed Entry data; problems are reported on the sheet. */
export function deriveSheet(input: EngineInput): DerivedSheet {
  return deriveWithState(input).sheet;
}

/** `deriveSheet` plus the engine state it ran in, for tests and tooling. */
export function deriveWithState(input: EngineInput): { sheet: DerivedSheet; state: EngineState } {
  const { character } = input;
  const level = character.level;
  const actor = createActorData(level);
  const state: EngineState = {
    level,
    actor,
    items: [],
    rules: [],
    rollOptions: new RollOptions(),
    synthetics: createSynthetics(),
    toggles: [],
    toggleState: normaliseToggles(character.toggles, input.toggleState),
    variantRules: { ...character.variantRules },
    actorView: {},
    entries: input.entries,
    catalogue: input.catalogue ?? null,
    needed: new Set(),
    derivedRanks: {},
    addRules: (item) => {
      const rules = createRulesFor(item, state);
      state.rules.push(...rules);
      return rules;
    },
    inventory: emptyInventoryState(),
    derived: null,
    strikes: [],
    battleForm: null,
    handsReallyFree: 2,
    spellcasting: { sources: [], unrecognised: [] },
  };
  state.actorView = createActorView(state, character);
  state.rollOptions.set("all", "self:type:character");
  state.rollOptions.set("all", `self:level:${level}`);
  // Immunities, weaknesses and resistances entered by hand (#22) sit in the actor's block before any Rule Element merges with them.
  seedManualIWR(state, character.iwr);
  // The Variant Rules in force (#12), for Rule Elements predicated on them.
  for (const [rule, on] of Object.entries(character.variantRules)) if (on) state.rollOptions.set("all", `variant:${VARIANT_OPTION_SLUGS[rule as keyof VariantRules] ?? rule}`);

  // Items from Selections, plus answer-only Choices (boosts, skill increases).
  const missing: DerivedSheet["missing"] = [];
  const boosts: { level: number; attribute: Attribute }[] = [];
  const skillIncreases: string[] = [];
  const potencyAnswers: SkillPotencyAnswer[] = [];
  let apexAnswer: Attribute | null = null;
  const choiceByKey = new Map<string, Choice>();
  const familiarChoices: FamiliarChoice[] = [];
  const stopped: UnsupportedRule[] = [];
  for (const lvl of character.levels) {
    if (lvl.number > level) continue;
    for (const choice of lvl.choices) {
      const key = choiceKeyOf(lvl.number, choice);
      choiceByKey.set(key, choice);
      // A Familiar ability's Rule Elements are the Familiar's, not the master's (#13).
      if (isFamiliarAbilityChoice(choice)) {
        familiarChoices.push({ level: lvl.number, choice });
        continue;
      }
      if (choice.source.kind === "progression" && choice.source.slot === "attribute-boosts") {
        for (const [k, v] of Object.entries(choice.answers ?? {})) if (/^boost(:\d+)?$/.test(k) && isAttribute(v)) boosts.push({ level: lvl.number, attribute: v });
        if (!choice.selection) continue;
      }
      if (choice.source.kind === "progression" && choice.source.slot === "skill-increase") {
        const skill = choice.answers?.skill;
        if (typeof skill === "string" && skill) skillIncreases.push(skill);
        if (!choice.selection) continue;
      }
      // Automatic Bonus Progression's Choices (#12): skill potency steps and the attribute apex.
      if (choice.source.kind === "progression" && choice.source.slot === "skill-potency") {
        const skill = choice.answers?.skill;
        potencyAnswers.push({ level: lvl.number, ordinal: choice.source.ordinal, skill: typeof skill === "string" ? skill : "" });
        if (!choice.selection) continue;
      }
      if (choice.source.kind === "progression" && choice.source.slot === "attribute-apex") {
        const attribute = choice.answers?.attribute;
        if (isAttribute(attribute)) apexAnswer = attribute;
        if (!choice.selection) continue;
      }
      if (!choice.selection) continue;
      const entry = input.entries.get(choice.selection.uuid);
      if (!entry) {
        missing.push({ uuid: choice.selection.uuid, choiceKey: key });
        continue;
      }
      const levelTaken = choice.source.kind === "feature" ? featureRowLevel(input.entries.get(choice.source.uuid), choice.source.id, lvl.number) : lvl.number;
      state.items.push(new EngineItem({ entry, answers: choice.answers, choiceKey: key, levelTaken }));
    }
  }
  linkGrantedItems(state, choiceByKey, stopped);
  for (const extra of input.extra ?? []) state.items.push(new EngineItem(extra));
  linkSessionGrants(state);
  // The inventory (#11): physical Entries join the item list; their Rule Elements apply only while equipped and invested.
  state.inventory = buildInventory(state, character.inventory, input.equipped);
  state.items.sort((a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role]);
  prepareConditionSiblings(state);
  setEffectRollOptions(state);
  setArmorRollOptions(state);

  // 1. Structural data. Under Dual Class (#12) the class the Level 1 class Choice holds is primary; the other merges.
  const primaryClass = state.items.find((i) => i.role === "class" && i.choiceKey === choiceKeyOf(1, { source: { kind: "class" } })) ?? state.items.find((i) => i.role === "class");
  for (const item of state.items) {
    switch (item.type) {
      case "ancestry": prepareAncestry(state, item); break;
      case "heritage": prepareHeritage(state, item); break;
      case "background": prepareBackground(state, item); break;
      case "class": prepareClass(state, item, item === primaryClass); break;
      case "feat": prepareFeat(state, item); break;
      case "deity": prepareDeity(state, item); break;
      default: break;
    }
  }
  prepareInventoryActorData(state);
  // A class whose key attribute options come from a feature (a psychic's conscious mind) resolves its answer only now;
  // so does a Dual Class key attribute taken from the second class's options (#12).
  const classItem = primaryClass;
  const keyAnswer = classItem?.answers.keyAbility;
  const attributesBuild = actor.system.build.attributes;
  if (classItem && isAttribute(keyAnswer) && attributesBuild.keyOptions.includes(keyAnswer) && (attributesBuild.boosts.class === null || (state.variantRules.dualClass && attributesBuild.boosts.class !== keyAnswer))) {
    attributesBuild.boosts.class = keyAnswer;
    actor.system.details.keyability.value = keyAnswer;
    const classDC = actor.system.proficiencies.classDCs[classItem.slug];
    if (classDC) classDC.attribute = keyAnswer;
  }
  for (const { level: at, attribute } of boosts) {
    const bucket = BOOST_BUCKETS.find((b) => b >= at) ?? 20;
    actor.system.build.attributes.boosts[bucket].push(attribute);
  }
  // Under Automatic Bonus Progression the apex is the Level 17 Choice's, never an item's (#12).
  if (state.variantRules.automaticBonusProgression) attributesBuild.apex = apexAnswer;
  prepareBuildData(state);
  for (const slug of skillIncreases) {
    const skill = actor.system.skills[slug];
    if (skill) skill.rank = Math.min(4, skill.rank + 1) as Rank;
  }
  // A rank Override stands in for the rank Foundry stores on the sheet: in place before any Rule Element reads it.
  applyRankOverrides(state, character.overrides);
  // Casting Sources (#10): the spellcasting proficiency floor, the focus pool and the caster roll options precede Rule Elements as in Foundry.
  state.spellcasting = collectCastingSources(state, character);

  // Rule Elements, sorted by priority within the item order.
  state.rules = createRules(state);
  applyGrantAlterations(state);

  // 2. applyAEs phase (MartialProficiency runs here at priority 9, before ActiveEffectLikes), then numeric roll options.
  // In-memory Entries (aura Effects, Conditions) add rules while the phase runs; they get their turn too.
  for (let i = 0; i < state.rules.length; i++) runPhase(state.rules[i]!, state, "onApplyActiveEffects");
  for (const ability of Object.values(actor.system.abilities)) ability.mod = Math.trunc(ability.mod) || 0;
  // Hands, bulk and investiture from the inventory, now that Strength and size are final; being over the threshold imposes encumbered.
  prepareHands(state);
  prepareBulk(state);
  applyRankOverrides(state, character.overrides);
  prepareMartialProficiencies(state, character.overrides);
  setStrengthRequirementOption(state);
  setNumericRollOptions(state);
  setDerivedRollOptions(state);

  // 3. beforeDerived phase: FlatModifiers, DamageDice, Notes and the rest register.
  for (let i = 0; i < state.rules.length; i++) runPhase(state.rules[i]!, state, "beforePrepareData");

  // Automatic Bonus Progression (#12): the potencies of the Level and the skill potencies chosen, before statistics derive.
  if (state.variantRules.automaticBonusProgression) applyAutomaticBonusProgression(state, skillPotencies(potencyAnswers));

  // 4. Statistics, then strikes.
  const derived = deriveStatistics(state, character);
  state.derived = derived;
  state.strikes = prepareStrikes(state);

  // 5. afterDerived phase: IWR, special statistics, a battle form's replacements.
  for (let i = 0; i < state.rules.length; i++) runPhase(state.rules[i]!, state, "afterPrepareData");

  // 6. The Familiar (#13), from the master's finished sheet.
  const familiar = deriveFamiliar(state, character, familiarChoices, derived, input.prompts ?? "open");

  // 7. What the Character should hold next, and what it still has to answer.
  const grants = collectGrants(state, choiceByKey, stopped);
  const temporaryGrants = collectTemporaryGrants(state);
  const prompts = [...collectPrompts(state, input.prompts ?? "open"), ...familiar.prompts];

  return { sheet: assembleSheet(state, character, derived, missing, { grants, prompts, stopped, temporaryGrants, familiar: familiar.view, familiarNeeded: familiar.needed }), state };
}

/** The Character's toggles under the table's: a boolean means checked. */
function normaliseToggles(own: Record<string, ToggleState> | undefined, table: Record<string, boolean | ToggleState> | undefined): Record<string, ToggleState> {
  const out: Record<string, ToggleState> = {};
  for (const [key, value] of Object.entries(own ?? {})) out[key] = { ...value };
  for (const [key, value] of Object.entries(table ?? {})) out[key] = { ...(out[key] ?? {}), ...(typeof value === "boolean" ? { checked: value } : value) };
  return out;
}

/** The level a feature-list row grants its feat at (Foundry sets the class feature's level from the row). */
function featureRowLevel(granter: Entry | undefined, id: string, fallback: number): number {
  const rows = granter?.system.items;
  const row = typeof rows === "object" && rows !== null ? (rows as Record<string, { level?: unknown }>)[id] : undefined;
  return typeof row?.level === "number" && row.level > 0 ? row.level : fallback;
}

/**
 * Link every granted item to its granter, mark preselected ChoiceSet flags, and drop items whose
 * grant chain is deeper than MAX_GRANT_DEPTH or revisits an Entry (reported as stopped).
 */
function linkGrantedItems(state: EngineState, choiceByKey: Map<string, Choice>, stopped: UnsupportedRule[]): void {
  const byKey = new Map(state.items.map((i) => [i.choiceKey ?? "", i]));
  for (const item of state.items) {
    const parsed = item.choiceKey ? parseChoiceKey(item.choiceKey) : undefined;
    const source = parsed?.source;
    if (!source || (source.kind !== "rule-element" && source.kind !== "feature")) continue;
    // The granter is the item with that Entry on the nearest Level at or below this one.
    const granter = state.items
      .filter((i) => i.uuid === source.uuid && i !== item && (parseChoiceKey(i.choiceKey ?? "")?.level ?? 0) <= parsed.level)
      .sort((a, b) => (parseChoiceKey(b.choiceKey ?? "")?.level ?? 0) - (parseChoiceKey(a.choiceKey ?? "")?.level ?? 0))[0];
    if (!granter) continue;
    item.grantedBy = granter;
    if (source.kind === "rule-element") {
      const rule = granter.rules[source.index];
      const preselect = rule?.key === "GrantItem" ? (rule.preselectChoices ?? rule.preselectCoices) : undefined;
      if (typeof preselect === "object" && preselect !== null) for (const flag of Object.keys(preselect)) item.preselected.add(flag);
    }
  }
  const dropped = new Set<EngineItem>();
  for (const item of state.items) {
    if (!item.grantedBy) continue;
    const chain = item.grantChain;
    const reason = chain.length > MAX_GRANT_DEPTH ? `chain deeper than ${MAX_GRANT_DEPTH}` : chain.some((i) => i.uuid === item.uuid) ? "chain revisits the Entry" : null;
    if (!reason) continue;
    dropped.add(item);
    const parsed = parseChoiceKey(item.choiceKey ?? "");
    stopped.push({
      key: parsed?.source.kind === "feature" ? "feature" : "GrantItem",
      support: "stopped",
      uuid: item.grantedBy.uuid,
      name: item.grantedBy.name,
      index: parsed?.source.kind === "rule-element" ? parsed.source.index : -1,
      selector: `${reason}: ${[...chain, item].map((i) => i.name).join(" -> ")}`,
    });
  }
  if (dropped.size > 0) state.items = state.items.filter((i) => !dropped.has(i));
  void byKey;
  void choiceByKey;
}

/** Session items (#18) name their granter by Session id; link them as Foundry links `grantedBy`. */
function linkSessionGrants(state: EngineState): void {
  const bySessionId = new Map(state.items.filter((i) => i.sessionId).map((i) => [i.sessionId!, i]));
  for (const item of state.items) {
    if (item.grantedBySessionId && !item.grantedBy) item.grantedBy = bySessionId.get(item.grantedBySessionId) ?? null;
  }
}

/** GrantItem `alterations` change the granted copy before it joins the Character (Foundry applies them to the source). */
function applyGrantAlterations(state: EngineState): void {
  for (const item of state.items) {
    const granter = item.grantedBy;
    const parsed = parseChoiceKey(item.choiceKey ?? "");
    if (!granter || parsed?.source.kind !== "rule-element") continue;
    const index = parsed.source.index;
    const rule = state.rules.find((r) => r.item === granter && r.index === index && r.key === "GrantItem");
    const data = rule?.data as GrantItemData | undefined;
    if (!rule || !data?.alterations?.length) continue;
    for (const alteration of data.alterations) {
      const problem = applyAlteration(rule, state, item, alteration);
      if (problem) rule.problems.push(problem);
    }
  }
}

/** Foundry's `Item#sourceId`-based duplicate check and grant bookkeeping, as a list of wanted grants. */
function collectGrants(state: EngineState, choiceByKey: Map<string, Choice>, stopped: UnsupportedRule[]): GrantView[] {
  const grants: GrantView[] = [];
  const seen = new Set<string>();
  const levelOf = (item: EngineItem): LevelNumber => parseChoiceKey(item.choiceKey ?? "")?.level ?? 1;
  const nameOf = (uuid: string | null): string | null => (uuid ? (state.entries.get(uuid)?.name ?? state.catalogue?.byUuid(uuid)?.n ?? null) : null);
  const slugOf = (uuid: string): string => state.entries.get(uuid)?.slug ?? state.catalogue?.byUuid(uuid)?.s ?? sluggify(nameOf(uuid) ?? uuid.slice(uuid.lastIndexOf(".") + 1));
  const stop = (granter: EngineItem, uuid: string): string | null => {
    const chain = [...granter.grantChain, granter];
    if (chain.length >= MAX_GRANT_DEPTH) return `chain deeper than ${MAX_GRANT_DEPTH}`;
    if (chain.some((i) => i.uuid === uuid)) return `chain revisits the Entry: ${[...chain.map((i) => i.name), nameOf(uuid) ?? uuid].join(" -> ")}`;
    return null;
  };
  const push = (view: GrantView): void => {
    if (seen.has(view.key)) return;
    seen.add(view.key);
    const held = choiceByKey.get(view.key);
    view.present = !!held?.selection && held.selection.uuid === view.uuid;
    view.loaded = !!view.uuid && state.entries.has(view.uuid);
    if (view.stopped && view.wanted) {
      stopped.push({ key: view.source.kind === "feature" ? "feature" : "GrantItem", support: "stopped", uuid: view.granter.uuid, name: view.granter.name, index: view.source.kind === "rule-element" ? view.source.index : -1, selector: view.stopped });
    }
    grants.push(view);
  };

  const flagsTaken = new Map<EngineItem, Set<string>>();
  for (const rule of state.rules) {
    if (rule.key !== "GrantItem" || !rule.handler) continue;
    const data = rule.data as unknown as GrantItemData;
    if (!data.uuid || data.inMemoryOnly) continue;
    const granter = rule.item;
    // Foundry tests a GrantItem's predicate once, before the grantee exists; here the grant is re-derived every pass,
    // so the grantee's own roll options are left out of its granter's test: a deity's (the `{ not: "deity" }` guard on
    // the Deity features) and a feat's `feat:<slug>` / `feature:<slug>` (the `{ not: "feature:draconic-benefactor" }`
    // guard on Kobold Breath, which would otherwise grant and remove the feature on alternate passes; #16). A grant
    // with `reevaluateOnUpdate` is re-tested by Foundry with the grantee in place, so it keeps every option.
    const granteeKey = granter.choiceKey ? keyOfChoice(levelOf(granter), { kind: "rule-element", uuid: granter.uuid, index: rule.index }) : null;
    const grantee = granteeKey ? state.items.find((i) => i.choiceKey === granteeKey) : undefined;
    const ownOption = grantee?.type === "deity"
      ? (o: string): boolean => o === "deity" || o.startsWith("deity:") || o.startsWith("self:deity:")
      : grantee?.type === "feat" && !data.reevaluateOnUpdate
        ? (o: string): boolean => o === `${grantee.isFeature ? "feature" : "feat"}:${grantee.slug}`
        : null;
    let options = ownOption ? new Set([...state.rollOptions.get()].filter((o) => !ownOption(o))) : undefined;
    // `self:has-familiar` is read as Foundry read it when the granter was created: without the granter's own slot upgrade (#13).
    if (JSON.stringify(rule.predicate).includes("self:has-familiar")) {
      options ??= new Set(state.rollOptions.get());
      if (familiarAbilitiesWithout(state, granter) > 0) options.add("self:has-familiar");
      else options.delete("self:has-familiar");
    }
    const wanted = !rule.ignored && rule.test(state, options);
    const uuid = resolveGrantUuid(rule, state);
    // A Session item's grants are collected separately (collectTemporaryGrants); an in-memory Entry's are not materialised.
    if (!granter.choiceKey) {
      if (wanted && uuid && !granter.sessionId) rule.problems.push(`grant of ${nameOf(uuid) ?? uuid} from an in-memory Entry is not materialised`);
      continue;
    }
    const taken = flagsTaken.get(granter) ?? new Set();
    const flag = uuid ? grantFlag(rule, slugOf(uuid), taken) : data.flag;
    if (flag) {
      taken.add(flag);
      flagsTaken.set(granter, taken);
    }
    const physical = uuid ? (state.entries.get(uuid)?.type ?? state.catalogue?.byUuid(uuid)?.t ?? "") : "";
    const level = levelOf(granter);
    const source = { kind: "rule-element" as const, uuid: granter.uuid, index: rule.index };
    push({
      key: keyOfChoice(level, source),
      level,
      source,
      granter: { uuid: granter.uuid, name: granter.name, choiceKey: granter.choiceKey },
      uuid,
      name: nameOf(uuid),
      wanted: wanted && !!uuid,
      present: false,
      loaded: false,
      flag,
      preselect: resolvedPreselections(rule, state),
      onDelete: { granter: data.onDeleteActions.granter ?? (PHYSICAL.has(physical) ? "detach" : "cascade"), grantee: data.onDeleteActions.grantee ?? "detach" },
      alterations: data.alterations,
      allowDuplicate: data.allowDuplicate,
      reevaluateOnUpdate: data.reevaluateOnUpdate,
      stopped: uuid ? stop(granter, uuid) : null,
      levelTaken: null,
    });
  }

  // Feature lists: the class features of a class, the ancestry features of an ancestry (Foundry's ABC `system.items`).
  for (const item of state.items) {
    if (!ABC_TYPES.includes(item.type) || !item.choiceKey) continue;
    const rows = item.system.items;
    if (typeof rows !== "object" || rows === null) continue;
    for (const [id, row] of Object.entries(rows as Record<string, { uuid?: unknown; level?: unknown; name?: unknown }>)) {
      if (!isItemUuid(row?.uuid)) continue;
      const rowLevel = typeof row.level === "number" ? row.level : (state.entries.get(row.uuid)?.level ?? 0);
      const granterLevel = levelOf(item);
      const at = Math.max(granterLevel, rowLevel);
      const level: LevelNumber = isLevelNumber(at) ? at : 20;
      const source = { kind: "feature" as const, uuid: item.uuid, id };
      push({
        key: keyOfChoice(level, source),
        level,
        source,
        granter: { uuid: item.uuid, name: item.name, choiceKey: item.choiceKey },
        uuid: row.uuid,
        name: nameOf(row.uuid) ?? (typeof row.name === "string" ? row.name : null),
        wanted: rowLevel <= state.level,
        present: false,
        loaded: false,
        flag: null,
        preselect: {},
        onDelete: { granter: "cascade", grantee: "detach" },
        alterations: [],
        allowDuplicate: false,
        reevaluateOnUpdate: true,
        stopped: stop(item, row.uuid),
        levelTaken: Math.max(1, rowLevel),
      });
    }
  }
  return grants;
}

const PHYSICAL = new Set(["ammo", "armor", "backpack", "book", "consumable", "equipment", "shield", "treasure", "weapon"]);

/**
 * Grants asked for by Session items (#18): a Condition or Effect fed as an extra Entry whose
 * GrantItem names another Condition or Effect. In-memory grants are created inside the engine
 * and left out; a grant of any other Entry type from a Session item is reported as a problem.
 */
function collectTemporaryGrants(state: EngineState): TemporaryGrantView[] {
  const out: TemporaryGrantView[] = [];
  for (const rule of state.rules) {
    if (rule.key !== "GrantItem" || !rule.handler) continue;
    const data = rule.data as unknown as GrantItemData;
    const granter = rule.item;
    if (!data.uuid || data.inMemoryOnly || !granter.sessionId || granter.choiceKey) continue;
    const wanted = !rule.ignored && rule.test(state);
    const uuid = resolveGrantUuid(rule, state);
    const entry = uuid ? state.entries.get(uuid) : undefined;
    const record = uuid ? state.catalogue?.byUuid(uuid) : undefined;
    const type = entry?.type ?? record?.t ?? null;
    if (type && type !== "condition" && type !== "effect") {
      if (wanted) rule.problems.push(`grant of ${entry?.name ?? record?.n ?? uuid} (${type}) from a Session Entry is not materialised`);
      continue;
    }
    let badge: number | null = null;
    let duration: TemporaryGrantView["duration"] = null;
    if (entry) {
      const copy = new EngineItem({ entry });
      for (const alteration of data.alterations) applyAlteration(rule, state, copy, alteration);
      badge = copy.badge?.value ?? null;
      const d = entry.type === "effect" ? (copy.system.duration as { unit?: unknown; value?: unknown; expiry?: unknown; sustained?: unknown } | undefined) : undefined;
      if (d && typeof d.unit === "string") duration = { unit: d.unit, value: typeof d.value === "number" ? d.value : 0, expiry: typeof d.expiry === "string" ? d.expiry : null, sustained: d.sustained === true };
    }
    const present = !!uuid && state.items.some((i) => i.sessionId && i.uuid === uuid && (data.allowDuplicate ? i.grantedBy === granter : true));
    out.push({
      granterId: granter.sessionId,
      granter: { uuid: granter.uuid, name: granter.name },
      index: rule.index,
      uuid,
      name: entry?.name ?? record?.n ?? null,
      type,
      slug: entry?.slug ?? record?.s ?? null,
      wanted: wanted && !!uuid,
      present,
      loaded: !!entry,
      allowDuplicate: data.allowDuplicate,
      onDelete: { granter: data.onDeleteActions.granter ?? "cascade", grantee: data.onDeleteActions.grantee ?? "detach" },
      alterations: data.alterations,
      badge,
      duration,
    });
  }
  return out;
}

/** The object `@actor.*` formulas and `{actor|...}` placeholders resolve against. */
function createActorView(state: EngineState, character: Character): Record<string, unknown> {
  const actor = state.actor;
  const skillsView = new Proxy({} as Record<string, unknown>, {
    get: (_t, slug) => (typeof slug === "string" ? actor.system.skills[slug] : undefined),
    has: (_t, slug) => typeof slug === "string" && slug in actor.system.skills,
  });
  const itemOfType = (type: string): unknown => state.items.find((i) => i.type === type) ?? null;
  return {
    name: character.name,
    type: "character",
    level: state.level,
    system: actor.system,
    flags: actor.flags,
    inventory: actor.inventory,
    abilities: actor.system.abilities,
    attributes: actor.system.attributes,
    skills: skillsView,
    hitPoints: actor.system.attributes.hp,
    // Foundry's `actor.conditions.<slug>`: the active Condition of that kind, with its value.
    conditions: new Proxy({} as Record<string, unknown>, {
      get: (_t, slug) => (typeof slug === "string" ? activeCondition(state, slug) : undefined),
      has: (_t, slug) => typeof slug === "string" && activeCondition(state, slug) !== null,
    }),
    get wornArmor() { return state.inventory.wornArmor; },
    get heldShield() { return state.inventory.heldShield; },
    get ancestry() { return itemOfType("ancestry"); },
    get heritage() { return itemOfType("heritage"); },
    get background() { return itemOfType("background"); },
    get class() { return itemOfType("class"); },
  };
}

/** The active Condition of a slug as `@actor.conditions.<slug>` sees it, or null. */
function activeCondition(state: EngineState, slug: string): { slug: string; name: string; value: number | null; active: true } | null {
  const found = state.items.filter((i) => i.type === "condition" && i.active && i.slug === slug).sort((a, b) => (b.badge?.value ?? 0) - (a.badge?.value ?? 0))[0];
  return found ? { slug, name: found.name, value: found.badge?.value ?? null, active: true } : null;
}

/** Foundry's creature `prepareDerivedData`: the death track from the Conditions held, dying max less doomed. */
function prepareDeathTrack(state: EngineState): void {
  const attributes = state.actor.system.attributes;
  attributes.wounded.max = Math.max(0, attributes.dying.max - 1);
  attributes.doomed.max = attributes.dying.max;
  for (const slug of ["doomed", "wounded", "dying"] as const) {
    const condition = activeCondition(state, slug);
    const status = attributes[slug];
    if (slug === "dying") status.max -= attributes.doomed.value;
    status.value = Math.min(condition?.value ?? 0, status.max);
  }
}

function deriveStatistics(state: EngineState, character: Character): DerivedStatistics {
  const { actor, synthetics, level } = state;
  const system = actor.system;
  prepareDeathTrack(state);

  // Proficiencies: sameAs links, duplicates and bonuses, after the beforeDerived phase (Foundry: prepareMartialProficiencies).
  prepareMartialProficiencies(state, character.overrides);

  // Hit points.
  const hpModifiers: Modifier[] = [
    createModifier({ slug: "ancestry-hp", label: "Ancestry HP", value: system.attributes.ancestryhp, type: "untyped" }),
    createModifier({ slug: "class-hp", label: "Class HP", value: system.attributes.classhp * level, type: "untyped" }),
    createModifier({ slug: "hp-con", label: ATTRIBUTE_LABELS.con, value: system.abilities.con.mod * Math.max(level, 1), type: "ability", ability: "con" }),
    ...extractModifiers(synthetics, ["hp"], { test: state.rollOptions.get(["hp"]) }),
    ...extractModifiers(synthetics, ["hp-per-level"], { test: state.rollOptions.get(["hp-per-level"]) }).map((m) => ({ ...m, value: m.value * level, original: m.original * level })),
  ];
  hpModifiers[2]!.adjustments = extractModifierAdjustments(synthetics.modifierAdjustments, ["con-based"], "hp-con");
  const hp = resolveModifiers(hpModifiers, state.rollOptions.get(["hp"]));
  system.attributes.hp.max = hp.total;
  system.attributes.hp.value = Math.min(system.attributes.hp.value || hp.total, hp.total);

  // Saves.
  const saves = Object.fromEntries(
    SAVES.map((save) => {
      const data = system.saves[save];
      const domains = [save, `${data.attribute}-based`, "saving-throw", "all"];
      const statistic = buildStatistic(state, {
        slug: save,
        label: SAVE_LABELS[save],
        attribute: data.attribute,
        rank: data.rank,
        domains,
        modifiers: armorSaveModifiers(state, save, domains),
        check: { type: "saving-throw" },
      });
      data.value = statistic.check.mod;
      return [save, statistic];
    }),
  ) as Record<SaveSlug, DerivedStatistic>;

  // Perception and initiative; the senses join Perception as in Foundry.
  prepareSenses(state);
  const perception = buildStatistic(state, {
    slug: "perception",
    label: "Perception",
    attribute: "wis",
    rank: system.perception.rank,
    domains: ["perception", "all"],
    check: { type: "perception-check" },
  });
  system.perception.value = perception.check.mod;
  const initiative = buildStatistic(state, {
    slug: "initiative",
    label: "Initiative",
    attribute: "wis",
    rank: system.perception.rank,
    domains: [...perception.domains, "initiative"],
    rollOptions: ["perception"],
    check: { type: "initiative", domains: ["perception-check"] },
  });

  // Skills, Lore included.
  const skills: Record<string, DerivedStatistic> = {};
  for (const [slug, skill] of Object.entries(system.skills)) {
    const spec = skill.lore
      ? { domains: [slug, "skill-check", "lore-skill-check", "int-skill-check", "all"], modifiers: [] as Modifier[] }
      : { domains: [slug, `${skill.attribute}-based`, "skill-check", `${skill.attribute}-skill-check`, "all"], modifiers: [] as Modifier[] };
    if (skill.armor) {
      const penalty = armorCheckPenalty(state, slug, spec.domains);
      if (penalty) spec.modifiers.push(penalty);
    }
    if (slug === "athletics") {
      spec.modifiers.push(createModifier({ slug: "no-crowbar", label: "No crowbar", type: "item", value: -2, predicate: ["action:force-open", "action:force-open:prying"], hideIfDisabled: true }));
    }
    const statistic = buildStatistic(state, {
      slug,
      label: skill.label,
      attribute: skill.attribute,
      rank: skill.rank,
      domains: spec.domains,
      modifiers: spec.modifiers,
      lore: skill.lore,
      check: { type: "skill-check" },
    });
    skill.value = statistic.check.mod;
    skills[slug] = statistic;
  }

  // Class DCs.
  const classDCs: Record<string, DerivedStatistic> = {};
  for (const [slug, classDC] of Object.entries(system.proficiencies.classDCs)) {
    const statistic = buildStatistic(state, {
      slug,
      label: classDC.label,
      attribute: classDC.attribute,
      rank: classDC.rank,
      domains: ["class", slug, "all"],
      check: { type: "check" },
    });
    classDCs[slug] = statistic;
    if (classDC.primary) {
      system.attributes.classDC = { rank: classDC.rank, value: statistic.dc.value };
      system.attributes.classOrSpellDC = { rank: classDC.rank, value: statistic.dc.value };
    }
  }

  // Base spellcasting proficiency, then one statistic per Spellcasting Entry extending it with the attribute and tradition (#10).
  const spellcastingRank = system.proficiencies.spellcasting.rank;
  const spellcasting =
    spellcastingRank > 0
      ? buildStatistic(state, { slug: "base-spellcasting", label: "Spellcasting", rank: spellcastingRank, domains: ["all", "spell-attack-dc"], check: { type: "attack-roll" } })
      : null;
  const spellcastingEntries = spellcastingStatistics(state, state.spellcasting.sources);

  // Armor Class (Foundry: createArmorStatistic): the worn armour's proficiency, item bonus and Dexterity cap, the lowest cap winning; a raised shield adds its bonus.
  const acRank = armorClassRank(state);
  const armorCap = armorDexterityCap(state);
  const dexCap = [...synthetics.dexterityModifierCaps, ...(armorCap ? [armorCap] : [])].reduce((lowest: { value: number; source: string } | null, candidate) => (lowest && lowest.value <= candidate.value ? lowest : candidate), null);
  const ac = buildStatistic(state, {
    slug: "armor",
    label: "Armor Class",
    attribute: "dex",
    rank: acRank,
    domains: ["all", "dex-based"],
    modifiers: [createAttributeModifier("dex", system.abilities.dex.mod, ["all", "ac", "dex-based"], dexCap?.value)],
    dc: { domains: ["ac"], modifiers: armorClassModifiers(state) },
  });
  system.attributes.ac.value = ac.dc.value;

  // Speeds: every movement type, with the armour and shield penalties.
  const speeds = prepareMovement(state, equipmentSpeedModifiers(state));

  return { hp: { modifiers: hp.modifiers, max: hp.total }, ac, saves, perception, initiative, skills, classDCs, spellcasting, spellcastingEntries, speeds, dexterityCap: dexCap };
}

function damageView(state: EngineState, damage: DerivedStrikeDamage): StrikeDamageView {
  return {
    formula: { ...damage.formula },
    breakdown: { failure: [...damage.breakdown.failure], success: [...damage.breakdown.success], criticalSuccess: [...damage.breakdown.criticalSuccess] },
    base: damage.base.map((b) => ({ diceNumber: b.diceNumber ?? 0, dieSize: b.dieSize ?? null, modifier: b.modifier ?? 0, damageType: b.damageType, damageTypeLabel: damageTypeLabel(state, b.damageType), category: b.category })),
    dice: damage.dice.map(damageDiceView),
    modifiers: damage.modifiers.map(modifierView),
    materials: [...damage.materials],
    domains: [...damage.domains],
    notes: damage.notes.map(noteView),
    ephemeralEffects: damage.ephemeralEffects.map((e: CheckEphemeralEffect) => ({ uuid: e.uuid, name: e.name, affects: e.affects, predicate: e.predicate, enabled: e.enabled, source: e.source, sourceName: e.sourceName })),
    criticalSpecialization: damage.criticalSpecialization,
    ignoredResistances: damage.ignoredResistances.map((r) => ({ ...r })),
  };
}

interface Collected {
  grants: GrantView[];
  prompts: ChoicePrompt[];
  stopped: UnsupportedRule[];
  temporaryGrants: TemporaryGrantView[];
  familiar: FamiliarView | null;
  familiarNeeded: string[];
}

function assembleSheet(state: EngineState, character: Character, derived: DerivedStatistics, missing: DerivedSheet["missing"], collected: Collected): DerivedSheet {
  const { actor } = state;
  const system = actor.system;
  const { overrideFor, used } = createOverrideLookup(character.overrides);

  const statisticView = (statistic: DerivedStatistic, overrideKey: string, useDC = false, finalPath?: string): StatisticView => {
    const traced = finalPath ? getProperty(actor, finalPath) : undefined;
    return buildStatisticView(statistic, overrideKey, overrideFor(overrideKey), { useDC, traced: typeof traced === "number" ? traced : undefined });
  };

  const strikeView = (strike: DerivedStrike): StrikeView => {
    const override = overrideFor(strike.overrideKey);
    const weapon = strike.weapon;
    return {
      slug: strike.slug,
      label: strike.label,
      source: strike.source,
      sourceName: strike.sourceName,
      category: weapon.category,
      group: weapon.group,
      baseType: weapon.baseType,
      traits: strike.weaponTraits,
      actionTraits: [...strike.actionTraits],
      melee: weapon.isMelee,
      ranged: weapon.isRanged,
      thrown: weapon.isThrown,
      rangeIncrement: weapon.range?.increment ?? null,
      maxRange: weapon.range?.max ?? null,
      handsHeld: weapon.handsHeld,
      ready: strike.ready,
      visible: strike.visible,
      handsAvailable: strike.handsAvailable,
      quantity: weapon.quantity,
      proficiencyRank: strike.proficiencyRank,
      derived: strike.totalModifier,
      override,
      value: override?.value ?? strike.totalModifier,
      modifiers: strike.modifiers.map(modifierView),
      breakdown: strike.breakdown,
      domains: [...strike.domains],
      variants: strike.variants.map((v) => ({ ...v })) as [StrikeVariantView, StrikeVariantView, StrikeVariantView],
      map: { map1: strike.map.map1, map2: strike.map.map2, label: strike.map.label },
      damage: strike.damage ? damageView(state, strike.damage) : null,
      versatileOptions: strike.versatileOptions.map((o) => ({ ...o })),
      altUsages: strike.altUsages.map(strikeView),
      battleForm: strike.battleForm,
      runes: { potency: weapon.runes.potency, striking: weapon.runes.striking, property: [...weapon.runes.property] },
      overrideKey: strike.overrideKey,
      inventoryId: weapon.inventoryId.startsWith("direct:") || weapon.rule || weapon.slug === "basic-unarmed" ? null : weapon.inventoryId,
      ammunition: strike.ammunition ? { baseType: strike.ammunition.baseType, selected: strike.ammunition.selected, compatible: strike.ammunition.compatible.map((c) => ({ ...c })) } : null,
      ...extrasView(strike.extras),
    };
  };

  const abilities = Object.fromEntries(
    ATTRIBUTES.map((attribute) => {
      const data = system.abilities[attribute];
      const override = overrideFor(`ability:${attribute}`);
      const view: AbilityView = { attribute, label: ATTRIBUTE_LABELS[attribute], derived: data.mod, base: data.base, override, value: override?.value ?? data.mod, overrideKey: `ability:${attribute}` };
      return [attribute, view];
    }),
  ) as Record<Attribute, AbilityView>;

  const hpOverride = overrideFor("hp-max");
  const hpDerived = system.attributes.hp.max;
  const hp: HitPointsView = {
    derived: hpDerived,
    override: hpOverride,
    value: hpOverride?.value ?? hpDerived,
    modifiers: derived.hp.modifiers.map(modifierView),
    breakdown: breakdownOf(derived.hp.modifiers),
    overrideKey: "hp-max",
    recoveryMultiplier: system.attributes.hp.recoveryMultiplier,
    recoveryAddend: system.attributes.hp.recoveryAddend,
    negativeHealing: system.attributes.hp.negativeHealing,
  };

  const saves = Object.fromEntries(SAVES.map((s) => [s, statisticView(derived.saves[s], s, false, `system.saves.${s}.value`)])) as Record<SaveSlug, StatisticView>;
  const skills = Object.fromEntries(Object.entries(derived.skills).map(([slug, st]) => [slug, statisticView(st, slug, false, `system.skills.${slug}.value`)]));
  const classDCs = Object.fromEntries(Object.entries(derived.classDCs).map(([slug, st]) => [slug, statisticView(st, `class-dc:${slug}`, true)]));
  const primaryClass = Object.entries(system.proficiencies.classDCs).find(([, c]) => c.primary)?.[0];
  const classDC = primaryClass && derived.classDCs[primaryClass] ? statisticView(derived.classDCs[primaryClass]!, "class-dc", true) : null;

  const speedView = (speed: DerivedSpeed): SpeedView => buildSpeedView(speed, `${speed.type}-speed`, overrideFor(`${speed.type}-speed`), system.movement.speeds[speed.type]?.value);
  const speeds = Object.fromEntries(MOVEMENT_TYPES.map((type) => [type, derived.speeds[type] ? speedView(derived.speeds[type]!) : null])) as SpeedViews;

  const proficiencyViews = (kind: ProficiencyKind): Record<string, ProficiencyView> => {
    const key = kind === "attack" ? "attacks" : "defenses";
    const out: Record<string, ProficiencyView> = {};
    for (const [slug, record] of Object.entries(system.proficiencies[key] as Record<string, MartialProficiencyData>)) {
      const overrideKey = rankOverrideKey(kind, slug);
      const override = overrideFor(overrideKey);
      const maxRankIndex = record.maxRank ? PROFICIENCY_RANKS.indexOf(record.maxRank) : -1;
      out[slug] = {
        slug,
        kind,
        label: proficiencyLabel(kind, slug, record),
        rank: record.rank,
        derived: state.derivedRanks[overrideKey] ?? record.rank,
        override,
        overrideKey,
        value: record.value ?? 0,
        breakdown: record.breakdown ?? "",
        visible: record.visible !== false,
        custom: record.custom === true || !!record.definition,
        definition: record.definition ? structuredClone(record.definition) : null,
        sameAs: record.sameAs ?? null,
        maxRank: maxRankIndex > 0 ? (maxRankIndex as Rank) : null,
        source: record.source ?? null,
        sourceName: record.sourceName ?? null,
      };
    }
    return out;
  };

  const unsupported: UnsupportedRule[] = [...collected.stopped];
  const problems: RuleProblem[] = [];
  for (const rule of state.rules) {
    const support = classifyKey(rule.key);
    if (rule.key && (support === "unsupported" || support === "unknown")) {
      unsupported.push({ key: rule.key, support, uuid: rule.item.uuid, name: rule.item.name, index: rule.index, selector: readSelector(rule.source) });
    }
    if (rule.problems.length > 0) problems.push({ key: rule.key, uuid: rule.item.uuid, name: rule.item.name, index: rule.index, messages: [...rule.problems] });
  }

  return {
    level: state.level,
    abilities,
    hp,
    ac: statisticView(derived.ac, "ac", true, "system.attributes.ac.value"),
    saves,
    perception: statisticView(derived.perception, "perception", false, "system.perception.value"),
    initiative: statisticView(derived.initiative, "initiative"),
    skills,
    classDC,
    classDCs,
    baseSpellcasting: derived.spellcasting ? statisticView(derived.spellcasting, "spellcasting") : null,
    spellcasting: spellcastingViews(state, character, state.spellcasting.sources, derived.spellcastingEntries, (st, source) => statisticView(st, `spell:${source.id}`)),
    unrecognisedCasting: state.spellcasting.unrecognised.map((u) => ({ ...u })),
    speeds,
    proficiencies: { attacks: proficiencyViews("attack"), defenses: proficiencyViews("defense") },
    strikes: state.strikes.map(strikeView),
    iwr: { immunities: system.attributes.immunities.map((i) => iwrView(state, i)), weaknesses: system.attributes.weaknesses.map((i) => iwrView(state, i)), resistances: system.attributes.resistances.map((i) => iwrView(state, i)) },
    resources: {
      tempHP: state.synthetics.tempHP.map((t) => ({ ...t, events: { ...t.events } })),
      fastHealing: state.synthetics.fastHealing.map((f) => ({ ...f, deactivatedBy: [...f.deactivatedBy] })),
      loseHitPoints: state.synthetics.loseHitPoints.map((l) => ({ ...l })),
      unrecoverableHP: system.attributes.hp.unrecoverable,
      special: Object.fromEntries(Object.entries(state.synthetics.resources).map(([k, r]) => [k, { ...r }])),
      crafting: structuredClone(system.crafting.entries),
      heroPoints: { ...system.resources.heroPoints },
      focus: { ...system.resources.focus, max: Math.max(0, Math.min(system.resources.focus.max, system.resources.focus.cap)) },
    },
    specialStatistics: Object.fromEntries([...state.synthetics.statistics.entries()].map(([slug, st]) => [slug, statisticView(st, `special:${slug}`)])),
    auras: [...state.synthetics.auras.values()].map((a) => ({ ...a, traits: [...a.traits], effects: a.effects.map((e) => ({ ...e })) })),
    battleForm: state.battleForm ? { uuid: state.battleForm.item.uuid, name: state.battleForm.item.name, ownUnarmed: state.battleForm.ownUnarmed } : null,
    reach: { ...system.attributes.reach },
    dexterityCap: derived.dexterityCap,
    size: system.traits.size.value,
    traits: [...new Set(system.traits.value)],
    senses: senseViews(state, system.perception.senses),
    languages: [...new Set([...system.details.languages.value, ...system.build.languages.granted.map((l) => l.slug)])],
    rollOptions: state.rollOptions.toJSON(),
    toggles: [...state.toggles],
    unsupported,
    problems,
    autoChanges: system.autoChanges,
    items: state.items.map((i) => ({
      uuid: i.uuid,
      name: i.name,
      type: i.type,
      choiceKey: i.choiceKey ?? null,
      level: parseChoiceKey(i.choiceKey ?? "")?.level ?? null,
      rules: i.rules.length,
      grantedBy: i.grantedBy ? { uuid: i.grantedBy.uuid, name: i.grantedBy.name, choiceKey: i.grantedBy.choiceKey ?? null } : null,
      openPrompts: collected.prompts.filter((p) => p.open && p.uuid === i.uuid && p.choiceKey === (i.choiceKey ?? null)).map((p) => p.flag),
      alterations: [...i.applied],
      inMemory: i.inMemory,
      sessionId: i.sessionId,
      active: i.active,
      overriddenBy: [...i.overriddenBy],
      badge: i.badge?.value ?? null,
    })),
    missing,
    danglingOverrides: character.overrides.filter((o) => !used.has(o.statistic)),
    prompts: collected.prompts,
    grants: collected.grants,
    temporaryGrants: collected.temporaryGrants,
    inventory: inventoryView(state),
    death: { dying: { ...system.attributes.dying }, wounded: { ...system.attributes.wounded }, doomed: { ...system.attributes.doomed } },
    needed: [...new Set([...state.needed, ...collected.familiarNeeded, ...collected.grants.filter((g) => g.wanted && g.uuid && !g.loaded && !g.stopped).map((g) => g.uuid!), ...collected.temporaryGrants.filter((g) => g.wanted && g.uuid && !g.loaded).map((g) => g.uuid!)])].sort(),
    familiar: collected.familiar,
  };
}

/** Every statistic an Override may name on this sheet, with its label and derived value. */
export function overrideTargets(sheet: DerivedSheet): { key: string; label: string; derived: number }[] {
  const targets: { key: string; label: string; derived: number }[] = [];
  for (const a of Object.values(sheet.abilities)) targets.push({ key: a.overrideKey, label: a.label, derived: a.derived });
  targets.push({ key: sheet.hp.overrideKey, label: "Hit Points", derived: sheet.hp.derived });
  targets.push({ key: sheet.ac.overrideKey, label: sheet.ac.label, derived: sheet.ac.derived });
  for (const s of Object.values(sheet.saves)) targets.push({ key: s.overrideKey, label: s.label, derived: s.derived });
  targets.push({ key: sheet.perception.overrideKey, label: sheet.perception.label, derived: sheet.perception.derived });
  targets.push({ key: sheet.initiative.overrideKey, label: sheet.initiative.label, derived: sheet.initiative.derived });
  if (sheet.classDC) targets.push({ key: sheet.classDC.overrideKey, label: `${sheet.classDC.label} DC`, derived: sheet.classDC.derived });
  for (const entry of sheet.spellcasting) if (entry.statistic && !entry.statisticOf) targets.push({ key: entry.statistic.overrideKey, label: `${entry.name} attack`, derived: entry.statistic.derived });
  for (const s of Object.values(sheet.skills)) targets.push({ key: s.overrideKey, label: s.label, derived: s.derived });
  for (const speed of Object.values(sheet.speeds)) if (speed) targets.push({ key: speed.overrideKey, label: speed.label, derived: speed.derived });
  for (const table of [sheet.proficiencies.attacks, sheet.proficiencies.defenses]) {
    for (const p of Object.values(table)) targets.push({ key: p.overrideKey, label: `${p.label} rank`, derived: p.derived });
  }
  for (const strike of sheet.strikes) {
    targets.push({ key: strike.overrideKey, label: `${strike.label} attack`, derived: strike.derived });
    for (const alt of strike.altUsages) targets.push({ key: alt.overrideKey, label: `${alt.label} (${alt.thrown ? "thrown" : "melee"}) attack`, derived: alt.derived });
  }
  for (const s of Object.values(sheet.specialStatistics)) targets.push({ key: s.overrideKey, label: s.label, derived: s.derived });
  return targets;
}

export { signed, isSkillSlug, SKILL_SLUGS };
